/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { forwardRef, useRef, useEffect, useImperativeHandle, useState } from "react";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";

export interface NumericTextBoxProps {
  id?: string;
  name?: string;
  value?: number | null;
  defaultValue?: number | null;
  placeholder?: string;
  min?: number | null;
  max?: number | null;
  step?: number;
  decimals?: number;
  format?: string;
  showSpinButton?: boolean;
  enabled?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  cssClass?: string;
  className?: string;
  width?: string;
  floatLabelType?: "Auto" | "Always" | "Never";
  label?: string;
  strictMode?: boolean;
  validateDecimalOnType?: boolean;
  showClearButton?: boolean;
  currency?: string;
  // Callbacks - Syncfusion compatible
  onChange?: (args: {
    value: number | null;
    previousValue?: number | null;
    /** Absent when the change came from the spin buttons rather than typing. */
    event?: React.ChangeEvent<HTMLInputElement>;
    target: { value: string; name?: string };
  }) => void;
  /**
   * METHOD SHORTHAND, deliberately: call sites annotate this handler with their own narrower arg
   * shapes (e.g. `{ target: { value: string | null } }` in ReviewFlowReviewLevelProperties). Method
   * syntax makes the parameter bivariant, which accepts them; a property arrow is contravariant and
   * rejects them all. `target` is also NON-OPTIONAL — an optional member here defeats bivariance in
   * both directions — and `emitChange` below always constructs it, so that is the accurate shape.
   */
  change?(args: {
    value: number | null;
    previousValue?: number | null;
    event?: React.ChangeEvent<HTMLInputElement>;
    target: { value: string; name?: string };
  }): void;
  created?: () => void;
  // The four focus/blur callbacks are always invoked with an argument, so their parameters are
  // declared required - an optional parameter would widen them to `| undefined` and reject the
  // call sites that annotate the event shape they read.
  focus?: (args: { event: React.FocusEvent<HTMLInputElement> }) => void;
  onFocus?: (args: React.FocusEvent<HTMLInputElement>) => void;
  blur?: (args: { event: React.FocusEvent<HTMLInputElement> }) => void;
  onBlur?: (
    args: React.FocusEvent<HTMLInputElement>,
    constraints?: { max?: number | null; min?: number | null },
  ) => void;
  // Style props
  style?: React.CSSProperties;
  sx?: SxProps<Theme>;
  // HTML data attributes
  [key: `data-${string}`]: string | number | undefined;
}

export interface NumericTextBoxRef {
  value: number | null;
  element: HTMLInputElement | null;
  focusIn: () => void;
  focusOut: () => void;
}

const NumericTextBoxComponent = forwardRef<NumericTextBoxRef, NumericTextBoxProps>((props, ref) => {
  const {
    id,
    name,
    value: propValue,
    defaultValue,
    placeholder,
    min,
    max,
    step = 1,
    decimals,
    showSpinButton = true,
    enabled = true,
    disabled: disabledProp,
    readOnly,
    cssClass,
    className,
    width,
    floatLabelType,
    label,
    strictMode = true,
    showClearButton: _showClearButton,
    currency,
    onChange,
    change,
    created,
    focus: focusProp,
    onFocus: onFocusProp,
    blur: blurProp,
    onBlur: onBlurProp,
    style,
    sx,
    ...restProps
  } = props;

  // Extract data-* attributes to forward to the DOM
  const dataAttributes = Object.fromEntries(Object.entries(restProps).filter(([key]) => key.startsWith("data-")));

  const inputRef = useRef<HTMLInputElement>(null);
  const initialValue = propValue ?? defaultValue;
  const [internalValue, setInternalValue] = useState<string>(initialValue?.toString() ?? "");
  const previousValueRef = useRef<number | null>(initialValue ?? null);

  const isDisabled = disabledProp ?? !enabled;

  useEffect(() => {
    if (created) {
      created();
    }
  }, []);

  useEffect(() => {
    if (propValue !== undefined) {
      setInternalValue(propValue?.toString() ?? "");
      previousValueRef.current = propValue ?? null;
    }
  }, [propValue]);

  useImperativeHandle(ref, () => ({
    get value() {
      const num = parseFloat(internalValue);
      return isNaN(num) ? null : num;
    },
    set value(val: number | null) {
      setInternalValue(val?.toString() ?? "");
    },
    get element() {
      return inputRef.current;
    },
    focusIn: () => inputRef.current?.focus(),
    focusOut: () => inputRef.current?.blur(),
  }));

  const formatValue = (val: number | null): string => {
    if (val === null) return "";
    // Coerce defensively: min/max props (and external values) may arrive as
    // numeric-looking strings, which pass the loose isNaN check but have no
    // .toFixed method. Normalize to a real number before formatting.
    const num = typeof val === "number" ? val : Number(val);
    if (isNaN(num)) return "";
    if (decimals !== undefined) {
      return num.toFixed(decimals);
    }
    return num.toString();
  };

  const parseValue = (str: string): number | null => {
    if (str === "" || str === "-") return null;
    const num = parseFloat(str);
    if (isNaN(num)) return null;
    return num;
  };

  const clampValue = (val: number | null): number | null => {
    if (val === null) return null;
    if (strictMode) {
      // min/max may arrive as strings from form config; coerce so we always
      // return a real number (not the raw prop) when clamping.
      const minNum = min != null ? Number(min) : null;
      const maxNum = max != null ? Number(max) : null;
      if (minNum != null && !isNaN(minNum) && val < minNum) return minNum;
      if (maxNum != null && !isNaN(maxNum) && val > maxNum) return maxNum;
    }
    return val;
  };

  const emitChange = (newValue: number | null, event?: React.ChangeEvent<HTMLInputElement>) => {
    const args = {
      value: newValue,
      previousValue: previousValueRef.current,
      event,
      target: { value: newValue?.toString() ?? "", name: name || id || "" },
    };

    if (onChange) onChange(args);
    if (change) change(args);

    previousValueRef.current = newValue;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    // Allow empty, minus sign, or valid number patterns
    if (inputValue === "" || inputValue === "-" || /^-?\d*\.?\d*$/.test(inputValue)) {
      setInternalValue(inputValue);
      const parsed = parseValue(inputValue);
      emitChange(parsed, e);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    let parsed = parseValue(internalValue);
    parsed = clampValue(parsed);

    const formatted = formatValue(parsed);
    setInternalValue(formatted);

    if (blurProp) blurProp({ event: e });
    if (onBlurProp) {
      onBlurProp(e, { max, min });
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (focusProp) focusProp({ event: e });
    if (onFocusProp) onFocusProp(e);
  };

  const handleIncrement = () => {
    if (isDisabled || readOnly) return;
    let current = parseValue(internalValue) ?? 0;
    current = clampValue(current + step) ?? current + step;
    const formatted = formatValue(current);
    setInternalValue(formatted);
    emitChange(current);
  };

  const handleDecrement = () => {
    if (isDisabled || readOnly) return;
    let current = parseValue(internalValue) ?? 0;
    current = clampValue(current - step) ?? current - step;
    const formatted = formatValue(current);
    setInternalValue(formatted);
    emitChange(current);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowUp") {
      e.preventDefault();
      handleIncrement();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      handleDecrement();
    }
  };

  const shrink = floatLabelType === "Always" ? true : floatLabelType === "Never" ? false : undefined;

  return (
    <TextField
      inputRef={inputRef}
      id={id}
      name={name}
      type="text"
      inputMode="decimal"
      value={internalValue}
      placeholder={placeholder}
      label={label}
      disabled={isDisabled}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onKeyDown={handleKeyDown}
      className={className || cssClass}
      style={style}
      slotProps={{
        input: {
          readOnly,
          endAdornment: showSpinButton ? (
            <InputAdornment position="end">
              <Box sx={{ display: "flex", flexDirection: "column", marginInlineEnd: "-0.5rem" }}>
                <IconButton
                  size="small"
                  onClick={handleIncrement}
                  disabled={isDisabled || readOnly}
                  sx={{ padding: 0, height: "1rem" }}
                  tabIndex={-1}
                >
                  <KeyboardArrowUpIcon sx={{ fontSize: "1rem" }} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={handleDecrement}
                  disabled={isDisabled || readOnly}
                  sx={{ padding: 0, height: "1rem" }}
                  tabIndex={-1}
                >
                  <KeyboardArrowDownIcon sx={{ fontSize: "1rem" }} />
                </IconButton>
              </Box>
            </InputAdornment>
          ) : currency ? (
            <InputAdornment position="end">{currency}</InputAdornment>
          ) : null,
        },
        htmlInput: {
          ...dataAttributes,
        },
        inputLabel: {
          shrink,
        },
      }}
      sx={{
        width: width ?? "100%",
        "& .MuiOutlinedInput-root": {
          height: "2rem",
          borderColor: "#666A84",
        },
        "& .MuiInputBase-input": {
          padding: "0.3125rem 0.625rem",
        },
        "& .MuiInputBase-input::placeholder": {
          color: "#666A84",
          opacity: 1,
        },
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "#666A84",
        },
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: "#6BBBE3",
          borderWidth: "0.0625rem",
        },
        "& .MuiOutlinedInput-root.Mui-disabled": {
          backgroundColor: "var(--light-blue-tag)",
        },
        ...sx,
      }}
    />
  );
});

NumericTextBoxComponent.displayName = "NumericTextBoxComponent";

export default NumericTextBoxComponent;
